'use client';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '@/context/TenantContext';
import { isAuthenticated } from '@/services/auth';
import { ROUTE_FEATURE_MAP } from '@/utils/features';
import styles from './page-feature-guard.module.scss';

// Routes that require authentication
const PROTECTED_PATHS = [
    '/dashboard',
    '/check-in',
    '/check-out',
    '/parking-list',
    '/finance',
    '/audit-log',
    '/settings',
    '/system-config',
    '/tenant-management',
    '/vehicle-details',
    '/item-details',
];

/**
 * PageFeatureGuard — lives in the root layout.
 * 1. Redirects unauthenticated users to /unauthorized for protected routes.
 * 2. Shows a locked screen for feature-gated routes the tenant hasn't enabled.
 */
export default function PageFeatureGuard({ children }: { children: React.ReactNode }) {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { tenantData, isSuperAdmin, loading: tenantLoading } = useTenant();
    const [authChecked, setAuthChecked] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);

    useEffect(() => {
        const auth = isAuthenticated();
        setAuthenticated(auth);
        setAuthChecked(true);
    }, [pathname]);

    const isProtected = PROTECTED_PATHS.some(
        (p) => pathname === p || pathname.startsWith(p + '/')
    );

    // Wait for client-side auth check before rendering protected routes
    if (isProtected && !authChecked) {
        return null;
    }

    // Not authenticated on a protected route → redirect to home (login page).
    // We use "/" not "/unauthorized" because the user simply needs to log in,
    // not because they are denied access.
    if (isProtected && authChecked && !authenticated) {
        navigate('/', { replace: true });
        return null;
    }

    // SYSTEM_OWNER bypasses all feature gates
    if (isSuperAdmin) {
        return <>{children}</>;
    }

    // Find if current route requires a feature
    const matchedEntry = Object.entries(ROUTE_FEATURE_MAP).find(
        ([route]) => pathname === route || pathname.startsWith(route + '/')
    );
    if (matchedEntry && (tenantLoading || !tenantData?.features)) {
        // Feature-gated route but data still loading → wait
        return null;
    }
    // No feature gate needed for non-protected routes
    if (!matchedEntry) {
        return <>{children}</>;
    }

    const [, requiredFeature] = matchedEntry;
    const isEnabled = !!tenantData?.features?.[requiredFeature];

    if (isEnabled) {
        return <>{children}</>;
    }

    // Feature is disabled for this tenant — show lock screen
    return <LockedPage featureKey={requiredFeature} />;
}

function LockedPage({ featureKey }: { featureKey: string }) {
    const featureLabel = featureKey.replace(/_/g, ' ');

    return (
        <div className={styles.lockedPage}>
            <div className={styles.lockedCard}>
                <div className={styles.iconWrap}>
                    <svg
                        className={styles.lockIcon}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                    </svg>
                </div>
                <h2 className={styles.title}>Feature Not Available</h2>
                <p className={styles.featureName}>{featureLabel}</p>
                <p className={styles.description}>
                    This feature is not included in your current subscription plan.
                    Contact your administrator to upgrade.
                </p>
            </div>
        </div>
    );
}
